import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { API_REQUEST } from '../../../core/actions';
import * as AssetsAPI from '../../../core/api/assets';
import { getJwtToken } from '../../../core/storage';
import { TOKEN_HEADER } from '../../../core/constants';
import TaskTemplateAdminDrawer, {
  AssetScope,
  DrawerFormState,
} from '../../taskTemplates/components/TaskTemplateAdminDrawer';
import {
  fetchAssetTemplates,
  fetchAssetGroups,
  fetchAssetItems,
  fetchAssetTaskOccurrences,
  fetchAssetTemplateOccurrence,
  deselectTemplateOccurrence,
  openAddTemplateDrawer,
  closeAddTemplateDrawer,
  openNodeActionDrawer,
  closeNodeActionDrawer,
  GET_ASSET_TEMPLATE,
} from '../actions';
import { getGroupDisplayName, getItemDisplayName } from '../helpers';
import AssetTree from '../components/AssetTree';
import AssetDetail from '../components/AssetDetail';
import AssetListView from '../components/AssetListView';
import AssetCardView from '../components/AssetCardView';
import { PageFilters } from '../../../core/components/PageFilters';
import { AssetRow } from '../listHelpers';
import { formatTemplateType } from '../models';
import AssetCartDrawer from '../components/AssetCartDrawer';
import AssetDetailHeader from '../components/AssetDetailHeader';
import { useAssetFetching } from '../hooks/useAssetFetching';
import { useOccurrenceContext } from '../hooks/useOccurrenceContext';
import { useAssetFilters } from '../hooks/useAssetFilters';
import { useAssetCart } from '../hooks/useAssetCart';
import { useNodeActionSubmit } from '../hooks/useNodeActionSubmit';
import { useAssetSelection } from '../hooks/useAssetSelection';
import { useOccurrenceDrawer } from '../hooks/useOccurrenceDrawer';
import { useAssetViewRefresh } from '../hooks/useAssetViewRefresh';
import { useAssetArchive } from '../hooks/useAssetArchive';
import AddTemplateDrawer from '../components/AddTemplateDrawer';
import AddToNodeDrawer from '../components/AddToNodeDrawer';
import ArchiveConfirmModal from '../components/ArchiveConfirmModal';
import { Drawer } from '../../../core/components/Drawer';
import { TaskDetails } from '../../taskTemplates/components/TaskDetails';
import OccurrenceDrawerBody from '../../taskTemplates/components/OccurrenceDrawerBody';

const TASK_DRAWER_WIDTH = 60;

type ViewMode = 'list' | 'card';

interface AssetsContainerProps {
  /** 'tree' keeps the hierarchy sidebar; 'site' is the flat site-manager page. */
  variant?: 'tree' | 'site';
}

const AssetsContainer: React.FC<AssetsContainerProps> = ({ variant = 'tree' }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const {
    site,
    siteRef,
    siteLabel,
    templateOccurrences,
    selectedOccurrenceId,
    tree,
    selectedTemplateId,
    selectedNodeId,
    expandedNodeIds,
    searchTerm,
    loading,
    error,
    addTemplateDrawerOpen,
    nodeActionDrawer,
    selectedSidebarItem,
    groups,
    items,
    activeTemplates,
    activeGroups,
    activeItems,
    linkableTaskOccurrences,
    linkableInventoryItems,
  } = useAssetFetching();

  const {
    occurrenceIdByTemplateId,
    occurrenceIdByTemplateIdRef,
    statusByTemplateId,
    workOrdersByTemplateId,
    deployedTemplateIds,
    openOccurrence,
    occurrenceGroups,
    occurrenceItems,
    occurrenceTemplates,
    groupOccurrenceIdByGroupId,
    itemOccurrenceIdByItemId,
  } = useOccurrenceContext({
    templateOccurrences,
    selectedOccurrenceId,
    activeGroups,
    activeItems,
    activeTemplates,
  });

  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return activeTemplates;
    const lower = searchTerm.toLowerCase();
    return activeTemplates.filter(
      (t: any) => t.name?.toLowerCase().includes(lower) || t.type?.toLowerCase().includes(lower)
    );
  }, [activeTemplates, searchTerm]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return activeGroups;
    const lower = searchTerm.toLowerCase();
    return activeGroups.filter((g: any) => g.name?.toLowerCase().includes(lower));
  }, [activeGroups, searchTerm]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return activeItems;
    const lower = searchTerm.toLowerCase();
    return activeItems.filter((i: any) => i.name?.toLowerCase().includes(lower));
  }, [activeItems, searchTerm]);

  // The flat views have no detail pane, so a row click marks the row; Edit
  // then acts through the same drawer the tree uses.
  const [selectedRow, setSelectedRow] = useState<AssetRow | null>(null);

  // Edit puts the table in cart mode: rows gain an Add control and nothing
  // reaches the site until the summary drawer is submitted.
  const {
    cartMode,
    cartTemplateIds,
    setCartTemplateIds,
    cartOpen,
    setCartOpen,
    deploying,
    cartRows,
    handleToggleCartMode,
    handleToggleCart,
    handleAddCartToSite,
    handleRemoveFromSite,
  } = useAssetCart({
    intl,
    activeTemplates,
    activeGroups,
    activeItems,
    occurrenceIdByTemplateId,
  });

  const { siteManagerTemplates, siteFilterConfigs } = useAssetFilters({
    intl,
    activeTemplates,
    deployedTemplateIds,
    cartMode,
  });

  const {
    componentTree,
    selectedNode,
    breadcrumb,
    handleBackToTemplates,
    handleSelectGroup,
    handleSelectItem,
    handleSelectSource,
    handleSelectNode,
    handleToggleNode,
    handleSearchChange,
    handleExpandAll,
    handleCollapseAll,
  } = useAssetSelection({ tree, selectedNodeId, selectedSidebarItem, groups, items });

  const handleSelectTemplate = useCallback(
    async (templateId: number) => {
      // On the catalog every template on screen is a deployment, so selecting
      // one loads the site's occurrence. Loading the blueprint here is what
      // left the page without an occurrence and sent edits to the master.
      if (variant === 'site') {
        const occurrenceId = occurrenceIdByTemplateIdRef.current.get(Number(templateId));
        if (occurrenceId) {
          dispatch(fetchAssetTemplateOccurrence(occurrenceId));
          return;
        }
      }
      const requestSite = site;
      dispatch({ type: `${GET_ASSET_TEMPLATE}_REQUEST` });
      try {
        const token = getJwtToken();
        const response = await AssetsAPI.getAssetTemplate({
          data: { id: templateId },
          options: {
            headers: { [TOKEN_HEADER]: token },
            siteId: requestSite,
          },
        });
        // A site switch while this was in flight would otherwise re-open the
        // template belonging to the site the user just left.
        if (siteRef.current !== requestSite) return;
        dispatch({ type: `${GET_ASSET_TEMPLATE}_SUCCESS`, response });
      } catch (err) {
        dispatch({ type: `${GET_ASSET_TEMPLATE}_FAILURE`, error: err });
      }
    },
    [dispatch, site]
  );

  // My Assets opens on the template edited most recently, so the detail pane is
  // never empty on arrival.
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    if (variant === 'site' || didAutoSelectRef.current) return;
    if (selectedTemplateId || selectedSidebarItem) {
      didAutoSelectRef.current = true;
      return;
    }
    if (!activeTemplates.length) return;

    const timestampOf = (template: any): number => {
      const raw = template?.modifiedAt || template?.updatedAt || template?.createdAt;
      const parsed = raw ? new Date(raw).getTime() : NaN;
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const latest = [...activeTemplates].sort(
      (a: any, b: any) => timestampOf(b) - timestampOf(a)
    )[0];
    if (latest?.id) {
      didAutoSelectRef.current = true;
      handleSelectTemplate(Number(latest.id));
    }
  }, [variant, activeTemplates, selectedTemplateId, selectedSidebarItem, handleSelectTemplate]);

  const handleOpenAddTemplate = () => {
    dispatch(openAddTemplateDrawer());
  };

  const handleCloseAddTemplate = () => {
    dispatch(closeAddTemplateDrawer());
  };

  const handleAddTemplateSubmit = (data: any) => {
    const imageFile = data.imageFile ?? null;
    dispatch({
      type: API_REQUEST,
      callAPI: AssetsAPI.postAssetTemplate,
      data: {
        name: data.name,
        type: data.type,
        version: data.version || 'v1',
        description: data.description || null,
      },
      action: 'CREATE_ASSET_TEMPLATE',
      // The image is a second request: the upload route is keyed on the
      // template id, which only exists once the row has been created. `navigate`
      // is the only saga hook handed the response body (see core/api/utils).
      navigate: (response: any) => {
        const templateId = Number(response?.id);
        dispatch(closeAddTemplateDrawer());
        if (imageFile && Number.isFinite(templateId)) {
          dispatch({
            type: API_REQUEST,
            callAPI: AssetsAPI.postAssetTemplateImage,
            data: { id: templateId, file: imageFile },
            action: 'UPLOAD_ASSET_TEMPLATE_IMAGE',
            voidCallback: () => dispatch(fetchAssetTemplates()),
            errorCallback: () => fetchAssetTemplates(),
          });
        } else {
          dispatch(fetchAssetTemplates());
        }
      },
    });
  };

  const handleAddNew = () => {
    if (selectedNode) {
      dispatch(openNodeActionDrawer('add', selectedNode, breadcrumb));
    } else {
      dispatch(openAddTemplateDrawer());
    }
  };

  const handleAddGroup = () => {
    dispatch(
      openNodeActionDrawer(
        'addGroup',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb
      )
    );
  };

  const handleAddAsset = () => {
    dispatch(
      openNodeActionDrawer(
        'addAsset',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb
      )
    );
  };

  const handleAddDailyTask = () => {
    dispatch(
      (openNodeActionDrawer as any)(
        'add',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb,
        'dailyTask'
      )
    );
  };

  const handleAddPrevMaint = () => {
    dispatch(
      (openNodeActionDrawer as any)(
        'add',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb,
        'prevMaint'
      )
    );
  };

  const handleAddProject = () => {
    dispatch(
      (openNodeActionDrawer as any)(
        'add',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb,
        'project'
      )
    );
  };

  const handleAddInventory = () => {
    dispatch(
      (openNodeActionDrawer as any)(
        'add',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb,
        'inventory'
      )
    );
  };

  const handleEditGroup = (group: any) => {
    const raw = group?.raw || group;
    if (!raw?.id) return;
    dispatch(
      (openNodeActionDrawer as any)(
        'editGroup',
        selectedNode || { name: getGroupDisplayName(raw), type: 'group' },
        breadcrumb,
        null,
        raw
      )
    );
  };

  const handleEditAsset = (item: any) => {
    const raw = item?.raw || item;
    if (!raw?.id) return;
    dispatch(
      (openNodeActionDrawer as any)(
        'editAsset',
        selectedNode || { name: getItemDisplayName(raw), type: 'item' },
        breadcrumb,
        null,
        raw
      )
    );
  };

  // Outside cart mode a row opens what the site runs, not the blueprint.
  const handleOpenRow = (row: AssetRow) => {
    setSelectedRow(row);
    const occurrenceId = occurrenceIdByTemplateId.get(row.id);
    if (occurrenceId) dispatch(fetchAssetTemplateOccurrence(occurrenceId));
  };

  const handleCloseOccurrence = () => {
    dispatch(deselectTemplateOccurrence());
    setSelectedRow(null);
  };

  const handleEditTemplate = (template: any) => {
    const raw = template?.raw || template;
    if (!raw?.id) return;
    dispatch(
      (openNodeActionDrawer as any)(
        'editTemplate',
        selectedNode || { name: raw.name, type: 'template' },
        breadcrumb,
        null,
        raw
      )
    );
  };

  const { selectedTemplateIdRef, selectedSidebarItemRef, refreshOpenAssetView } =
    useAssetViewRefresh({
      selectedTemplateId,
      selectedOccurrenceId: selectedOccurrenceId ?? null,
      selectedSidebarItem,
      onSelectTemplate: handleSelectTemplate,
    });

  const {
    archiveTarget,
    handleCancelArchive,
    archiveSubmitting,
    archiveError,
    handleArchiveNode,
    handleConfirmArchive,
    handleUnarchiveNode,
    handleArchiveTask,
    handleUnlinkInventory,
  } = useAssetArchive({
    site,
    selectedOccurrenceId: selectedOccurrenceId ?? null,
    selectedTemplateIdRef,
    selectedSidebarItemRef,
    refreshOpenAssetView,
  });

  const handleLinkDrop = useCallback(
    async (
      targetType: 'template' | 'group',
      targetId: number,
      dragType: 'group' | 'item',
      dragId: number
    ) => {
      try {
        const token = getJwtToken();
        const opts = { headers: { [TOKEN_HEADER]: token }, siteId: site };

        if (targetType === 'template' && dragType === 'group') {
          await AssetsAPI.linkTemplateGroup({
            data: { id: targetId, assetGroupId: dragId },
            options: opts,
          });
          dispatch(fetchAssetTemplates());
          dispatch(fetchAssetGroups());
        } else if (targetType === 'template' && dragType === 'item') {
          await AssetsAPI.linkTemplateItem({
            data: { id: targetId, assetItemId: dragId },
            options: opts,
          });
          dispatch(fetchAssetTemplates());
          dispatch(fetchAssetItems());
        } else if (targetType === 'group' && dragType === 'group') {
          if (targetId === dragId) return;
          await AssetsAPI.addGroupChild({
            data: { id: targetId, childGroupId: dragId },
            options: opts,
          });
          dispatch(fetchAssetGroups());
        } else if (targetType === 'group' && dragType === 'item') {
          await AssetsAPI.linkGroupItem({
            data: { id: targetId, assetItemId: dragId },
            options: opts,
          });
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
        }

        refreshOpenAssetView();
      } catch {
        // noop
      }
    },
    [dispatch, site, handleSelectTemplate]
  );

  const handleCloseNodeDrawer = () => {
    dispatch(closeNodeActionDrawer());
  };

  const isGroupOrAssetContext = !!selectedSidebarItem;

  // Both pages open on the flat table; the site-manager page hides the
  // hierarchy entirely, while the tree page swaps to a detail pane on select.
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const isSiteManagerView = variant === 'site';

  // My Assets falls back to the flat table whenever nothing is selected in the
  // tree; the site-manager page is always flat.
  // My Assets is the blueprint editor and always shows the tree plus a detail
  // pane; only the catalog has the flat list/tile views.
  const showFlatViews = isSiteManagerView && !selectedOccurrenceId;

  // The deployment in context. selectedOccurrenceId covers opening a row from
  // the table; the lookup covers reaching the same template through the
  // sidebar, which fetches the blueprint and leaves that id unset.
  const activeOccurrenceId = useMemo(() => {
    if (!isSiteManagerView) return null;
    if (selectedOccurrenceId) return Number(selectedOccurrenceId);
    // Fall back to whichever template the open tree belongs to. Selection state
    // varies with how the page was reached; the tree root does not.
    const openTemplateId = Number(tree?.rawId ?? selectedTemplateId ?? 0);
    return openTemplateId ? occurrenceIdByTemplateId.get(openTemplateId) ?? null : null;
  }, [isSiteManagerView, selectedOccurrenceId, tree, selectedTemplateId, occurrenceIdByTemplateId]);

  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [taskDrawerAssetScope, setTaskDrawerAssetScope] = useState<AssetScope | undefined>(
    undefined
  );
  const {
    selectedOccurrence,
    hasTemplate: occurrenceHasTemplate,
    occurrenceTemplate,
    isOccurrenceTemplateLoading,
    occurrenceDrawerError,
    isSavingOccurrenceDrawer,
    handleOpenOccurrence,
    handleCloseOccurrenceDrawer,
    handleOccurrenceSaved,
    handleOccurrenceDrawerSave,
  } = useOccurrenceDrawer({ refreshOpenAssetView });

  const [taskDrawerAddDefaults, setTaskDrawerAddDefaults] = useState<
    Partial<DrawerFormState> | undefined
  >(undefined);
  const [taskDrawerTitle, setTaskDrawerTitle] = useState<string | undefined>(undefined);

  const refreshAssetsAfterTaskCreate = () => {
    dispatch(fetchAssetGroups());
    dispatch(fetchAssetItems());
    dispatch(fetchAssetTaskOccurrences());
    refreshOpenAssetView();
    return undefined;
  };

  const handleNodeActionSubmit = useNodeActionSubmit({
    intl,
    site,
    isSiteManagerView,
    nodeActionDrawer,
    selectedSidebarItem,
    selectedOccurrenceId,
    activeOccurrenceId,
    groupOccurrenceIdByGroupId,
    itemOccurrenceIdByItemId,
    refreshOpenAssetView,
    handleSelectTemplate,
    setTaskDrawerAssetScope,
    setTaskDrawerAddDefaults,
    setTaskDrawerTitle,
    setTaskDrawerOpen,
  });

  return (
    <div className={`assets-page ${isSiteManagerView ? 'assets-page--flat' : ''}`}>
      {isSiteManagerView && !showFlatViews && (
        <div className="assets-page__detail-actions assets-page__detail-actions--top">
          <button
            type="button"
            className="btn btn-link assets-page__back-btn"
            onClick={handleCloseOccurrence}
          >
            <i className="uil uil-arrow-left" />
            <span>{intl.formatMessage({ id: 'assets.list.back' })}</span>
          </button>
        </div>
      )}

      {!showFlatViews && (
        <AssetDetailHeader
          node={selectedNode}
          breadcrumb={breadcrumb}
          onAddNew={handleAddNew}
          onAddTemplate={handleOpenAddTemplate}
          onAddGroup={handleAddGroup}
          onAddAsset={handleAddAsset}
          onEditGroup={handleEditGroup}
          onEditAsset={handleEditAsset}
          onEditTemplate={handleEditTemplate}
          onArchiveNode={handleArchiveNode}
          onUnarchiveNode={handleUnarchiveNode}
          showStructureActions={!isSiteManagerView}
          imageUrl={
            isSiteManagerView
              ? openOccurrence?.assetTemplate?.image?.downloadUrl ?? null
              : tree?.raw?.image?.downloadUrl ?? null
          }
          areaLabel={
            isSiteManagerView
              ? formatTemplateType(openOccurrence?.assetTemplate?.type) ?? null
              : formatTemplateType(tree?.raw?.type) ?? null
          }
          deployedAt={isSiteManagerView ? openOccurrence?.deployedAt ?? null : null}
        />
      )}

      <div className="assets-page__row">
        {/* The catalog is flat until a deployment is opened; then the sidebar
          shows that deployment's own hierarchy. */}
        {(!isSiteManagerView || !showFlatViews) && (
          <div className="assets-page__sidebar">
            <AssetTree
              templates={isSiteManagerView ? occurrenceTemplates : filteredTemplates}
              groups={isSiteManagerView ? occurrenceGroups : filteredGroups}
              items={isSiteManagerView ? occurrenceItems : filteredItems}
              tree={componentTree}
              selectedNodeId={selectedNodeId}
              selectedTemplateId={selectedTemplateId}
              selectedSidebarItem={selectedSidebarItem}
              expandedNodeIds={expandedNodeIds}
              searchTerm={searchTerm}
              onSelectNode={handleSelectNode}
              onToggleNode={handleToggleNode}
              onSearchChange={handleSearchChange}
              onExpandAll={handleExpandAll}
              onCollapseAll={handleCollapseAll}
              onSelectTemplate={handleSelectTemplate}
              onSelectGroup={handleSelectGroup}
              onSelectItem={handleSelectItem}
              onBackToTemplates={handleBackToTemplates}
              onLinkDrop={handleLinkDrop}
            />
          </div>
        )}

        <div className="assets-page__content">
          {showFlatViews && (
            <div className="assets-page__toolbar filters-actions-container mb-3">
              <PageFilters
                filters={siteFilterConfigs}
                menuLabel={intl.formatMessage({ id: 'filters' })}
              />

              <div className="page-actions assets-page__toolbar-actions">
                <button
                  type="button"
                  className="btn btn-danger assets-page__edit-btn"
                  onClick={handleToggleCartMode}
                  title={intl.formatMessage({ id: 'assets.list.editHint' })}
                >
                  <i className={`uil uil-${cartMode ? 'times' : 'edit'}`} />
                  <span>
                    {cartMode
                      ? intl.formatMessage({ id: 'assets.cart.cancel' })
                      : intl.formatMessage({ id: 'assets.list.edit' })}
                  </span>
                </button>

                <div className="assets-page__view-toggle">
                  <button
                    type="button"
                    className={`assets-page__view-btn ${
                      viewMode === 'list' ? 'assets-page__view-btn--active' : ''
                    }`}
                    onClick={() => setViewMode('list')}
                    title={intl.formatMessage({ id: 'assets.view.list' })}
                    aria-pressed={viewMode === 'list'}
                  >
                    <i className="uil uil-list-ul" />
                  </button>
                  <button
                    type="button"
                    className={`assets-page__view-btn ${
                      viewMode === 'card' ? 'assets-page__view-btn--active' : ''
                    }`}
                    onClick={() => setViewMode('card')}
                    title={intl.formatMessage({ id: 'assets.view.card' })}
                    aria-pressed={viewMode === 'card'}
                  >
                    <i className="uil uil-apps" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {showFlatViews && viewMode === 'list' && (
            <AssetListView
              templates={siteManagerTemplates}
              groups={activeGroups}
              items={activeItems}
              statusByTemplateId={statusByTemplateId}
              workOrdersByTemplateId={workOrdersByTemplateId}
              selectedRowKey={selectedRow?.key ?? null}
              onSelectRow={handleOpenRow}
              cartMode={cartMode}
              deployedTemplateIds={deployedTemplateIds}
              cartTemplateIds={cartTemplateIds}
              onToggleCart={handleToggleCart}
              onRemoveFromSite={handleRemoveFromSite}
            />
          )}

          {showFlatViews && viewMode === 'card' && (
            <AssetCardView
              templates={siteManagerTemplates}
              groups={activeGroups}
              items={activeItems}
              statusByTemplateId={statusByTemplateId}
              workOrdersByTemplateId={workOrdersByTemplateId}
              selectedRowKey={selectedRow?.key ?? null}
              onSelectRow={handleOpenRow}
            />
          )}

          {/* Only while there is nothing to show. Swapping an open detail pane
              for a spinner on every reload unmounted it, and local pane state --
              the task search box, the active tab -- went with it. */}
          {!showFlatViews && loading && !selectedNode && (
            <div className="assets-page__loading">
              <i className="uil uil-spinner-alt uil-spin" />
              <p>Loading template...</p>
            </div>
          )}
          {!showFlatViews && error && (
            <div className="assets-page__error">
              <i className="uil uil-exclamation-triangle" />
              <p>Failed to load template: {String(error)}</p>
            </div>
          )}
          {showFlatViews && cartMode && (
            <AssetCartDrawer
              open={cartOpen}
              siteLabel={siteLabel}
              rows={cartRows}
              submitting={deploying}
              onToggle={() => setCartOpen((prev) => !prev)}
              onRemove={handleToggleCart}
              onClear={() => setCartTemplateIds([])}
              onSubmit={handleAddCartToSite}
            />
          )}

          {!showFlatViews && (!loading || !!selectedNode) && (
            <AssetDetail
              node={selectedNode}
              onAddDailyTask={handleAddDailyTask}
              onAddPrevMaint={handleAddPrevMaint}
              onAddProject={handleAddProject}
              onAddInventory={handleAddInventory}
              onArchiveTask={handleArchiveTask}
              onOpenOccurrence={handleOpenOccurrence}
              onUnlinkInventory={handleUnlinkInventory}
              onSelectSource={handleSelectSource}
            />
          )}
        </div>

        <ArchiveConfirmModal
          target={archiveTarget}
          isSubmitting={archiveSubmitting}
          error={archiveError}
          onConfirm={handleConfirmArchive}
          onCancel={handleCancelArchive}
        />
      </div>

      {addTemplateDrawerOpen && (
        <AddTemplateDrawer
          isOpen
          onClose={handleCloseAddTemplate}
          onSubmit={handleAddTemplateSubmit}
        />
      )}

      {nodeActionDrawer && nodeActionDrawer.open && (
        <AddToNodeDrawer
          isOpen
          mode={nodeActionDrawer.mode}
          node={nodeActionDrawer.node}
          breadcrumb={nodeActionDrawer.breadcrumb}
          groups={activeGroups}
          taskOccurrences={linkableTaskOccurrences}
          inventoryItems={linkableInventoryItems}
          isAssetContext={isGroupOrAssetContext}
          initialItemType={nodeActionDrawer.initialItemType}
          editData={nodeActionDrawer.editData}
          occurrenceStatus={selectedOccurrenceId ? openOccurrence?.status ?? null : undefined}
          onClose={handleCloseNodeDrawer}
          onSubmit={handleNodeActionSubmit}
        />
      )}

      <TaskTemplateAdminDrawer
        isOpen={taskDrawerOpen}
        mode={taskDrawerOpen ? 'add' : null}
        addDefaults={taskDrawerAddDefaults}
        assetScope={taskDrawerAssetScope}
        titleOverride={taskDrawerTitle}
        onClose={() => {
          setTaskDrawerOpen(false);
          setTaskDrawerAssetScope(undefined);
          setTaskDrawerAddDefaults(undefined);
          setTaskDrawerTitle(undefined);
        }}
        onSaved={(saved) => {
          const scope = taskDrawerAssetScope;
          const materializeOccurrence = async () => {
            try {
              if (!saved?.id || !site) return;
              const token = getJwtToken();
              const opts = { headers: { [TOKEN_HEADER]: token }, siteId: site };
              const ensureResult: any = await AssetsAPI.postEnsureTaskOccurrence({
                data: { taskId: saved.id },
                options: opts,
              });
              const occurrenceId: number | undefined = ensureResult?.occurrence?.id;
              const wantsAssetPatch =
                occurrenceId != null &&
                (scope?.assetGroupId != null ||
                  scope?.assetItemId != null ||
                  scope?.assetTemplateId != null);
              if (wantsAssetPatch) {
                const patchBody: any = { id: occurrenceId };
                if (scope?.assetGroupId != null) patchBody.assetGroupId = scope.assetGroupId;
                if (scope?.assetItemId != null) patchBody.assetItemId = scope.assetItemId;
                if (scope?.assetTemplateId != null) {
                  patchBody.assetTemplateId = scope.assetTemplateId;
                }
                await AssetsAPI.patchTaskOccurrence({ data: patchBody, options: opts });
              }
            } catch {
              // scheduler will materialize the occurrence on its next tick if this fails
            } finally {
              refreshAssetsAfterTaskCreate();
            }
          };
          materializeOccurrence();
        }}
      />

      <Drawer
        expanded={!!selectedOccurrence}
        maximized={false}
        expandedWidth={TASK_DRAWER_WIDTH}
        overlay
        onBackdropClick={handleCloseOccurrenceDrawer}
      >
        {/* TaskDetails reads everything off the template, so hold the drawer
            until it has arrived -- handing it null renders the empty editor. */}
        {selectedOccurrence && occurrenceHasTemplate && (
          <>
            {isOccurrenceTemplateLoading || !occurrenceTemplate ? (
              <div className="assets-page__loading">
                <i className="uil uil-spinner-alt uil-spin" />
              </div>
            ) : (
              <TaskDetails
                template={occurrenceTemplate}
                onClose={handleCloseOccurrenceDrawer}
                onSave={handleOccurrenceSaved}
              />
            )}
          </>
        )}

        {/* An occurrence raised against an asset with no template behind it has
            nothing for TaskDetails to edit, so it keeps the occurrence form. */}
        {selectedOccurrence && !occurrenceHasTemplate && (
          <OccurrenceDrawerBody
            occurrence={selectedOccurrence}
            onClose={handleCloseOccurrenceDrawer}
            onSave={handleOccurrenceDrawerSave}
            isSaving={isSavingOccurrenceDrawer}
            error={occurrenceDrawerError}
          />
        )}
      </Drawer>
    </div>
  );
};

export default AssetsContainer;
